import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import {
  useDialedDigitExtractionServerFieldCreateNew,
} from 'routes/DialedDigitExtractionServer/hooks/useDialedDigitExtractionServerFieldCreateNew';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import DialedDigitExtractionServerForm from 'routes/DialedDigitExtractionServer/components/DialedDigitExtractionServerForm';
import { Ref } from 'routes/DialedDigitExtractionServer/components/DialedDigitExtractionServerForm/DialedDigitExtractionServerForm';
import { RootState } from 'data/root.reducers';
import * as dialedDigitExtractionServerSelectors from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.selectors';
import * as networkActions from 'data/network/network.actions';
import * as dialedDigitExtractionServerActions from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as deliveryFunctionNetworkInterfaceActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as networkSelectors from 'data/network/network.selectors';
import { DialedDigitExtractionServer } from 'data/dialedDigitExtractionServer/dialedDigitExtractionServer.types';
import { FormTemplateSection } from 'data/template/template.types';
import { dialedDigitExtractionServerTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions, adaptNetworkIdField } from '../../utils/dialedDigitExtractionServerTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/dialedDigitExtractionServerDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  dialedDigitExtractionServerTemplateDef: adaptTemplateVisibilityMode(dialedDigitExtractionServerTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  selectedDialedDigitExtractionServer: dialedDigitExtractionServerSelectors
    .getSelectedDialedDigitExtractionServer(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedDialedDigitExtractionServer: dialedDigitExtractionServerSelectors
    .getSelectedDialedDigitExtractionServer(state)[0],
  dialedDigitExtractionServer: dialedDigitExtractionServerSelectors.getDialedDigitExtractionServer(state),
  networkInterfaceList: networkSelectors.getNetworks(state),
});

const mapDispatch = {
  getNetworkInterfaceList: deliveryFunctionNetworkInterfaceActions.getDeliveryFunctionNetworkInterfaceList,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToDialedDigitExtractionServerList: dialedDigitExtractionServerActions.goToDialedDigitExtractionServerList,
  updateDialedDigitExtractionServer: dialedDigitExtractionServerActions.updateDialedDigitExtractionServer,
  getDialedDigitExtractionServer: dialedDigitExtractionServerActions.getDialedDigitExtractionServer,
  getNetworks: networkActions.getNetworks,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const DialedDigitExtractionServerEdit = ({
  history,
  selectedDialedDigitExtractionServer,
  originalSelectedDialedDigitExtractionServer,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  networkInterfaceList,
  dialedDigitExtractionServerTemplateDef,
  getNetworkInterfaceList,
  goToDialedDigitExtractionServerList,
  updateDialedDigitExtractionServer,
  getDialedDigitExtractionServer,
  getNetworks,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<DialedDigitExtractionServer>>({});
  const [template, setTemplate] = useState(dialedDigitExtractionServerTemplateDef);
  const { t } = useTranslation();

  const id = selectedDialedDigitExtractionServer?.id;

  const [handleFieldCreateNew] = useDialedDigitExtractionServerFieldCreateNew(
    (field) => handleFieldUpdate(field),
  );

  useEffect(() => {
    if (id) {
      getDialedDigitExtractionServer(deliveryFunctionId, id);
      getNetworks(deliveryFunctionId);
    }
  }, [deliveryFunctionId, id, getDialedDigitExtractionServer, getNetworks]);

  useEffect(() => {
    setTemplate(adaptTemplateOptions(template, networkInterfaceList));
  }, [networkInterfaceList]);

  useEffect(() => {
    if (selectedDialedDigitExtractionServer != null && template != null) {
      setFormData(selectedDialedDigitExtractionServer);

      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate.main = adaptNetworkIdField(template.main as FormTemplateSection, selectedDialedDigitExtractionServer);
      setTemplate(clonedTemplate);
    }
  }, [selectedDialedDigitExtractionServer, id]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const dialedDigitExtractionServerData = formRef.current.getValues();
    const payload = {
      data: dialedDigitExtractionServerData,
      auditDetails: getAuditDetailsToSubmit(
        dialedDigitExtractionServerData,
        originalSelectedDialedDigitExtractionServer,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload) {
      updateDialedDigitExtractionServer(deliveryFunctionId, selectedDialedDigitExtractionServer?.id, payload, history);
    }
  };

  const handleCancel = () => goToDialedDigitExtractionServerList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as DialedDigitExtractionServer);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <DialedDigitExtractionServerForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={t('dialedDigitExtractionServer.edit.title')}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="DdeServerEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('ss8Distributor.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(DialedDigitExtractionServerEdit));
